import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { noop } from 'lib/utils';

import Text from '../Text';
import TextareaField from './TextareaField';

const WRAP_TYPES = ['hard', 'soft', 'off'];

const Textarea = forwardRef(
  (
    {
      autoComplete = 'off',
      className,
      cols = 20,
      defaultValue,
      disabled = false,
      error = false,
      full = false,
      label,
      name,
      maxLength = null,
      message,
      minLength = null,
      placeholder,
      required = false,
      rows = 5,
      value = '',
      spellCheck = false,
      wrap = 'soft',
      onBlur = noop,
      onClick = noop,
      onChange = noop,
      onFocus = noop,
      onInvalid = noop,
      onKeyDown = noop,
      onKeyUp = noop,
      ...rest
    },
    ref
  ) => (
    <div
      className={twMerge(
        'relative flex flex-col text-sm text-neutral-low-100 lg:text-base',
        full ? 'w-full' : 'w-fit',
        className
      )}
      {...rest}
    >
      {label && (
        <Text
          as="label"
          className="mb-2 inline-block select-none dark:text-neutral-high-100"
          htmlFor={label}
        >
          {required ? `${label}*` : label}
        </Text>
      )}
      <TextareaField
        ref={ref}
        autoComplete={autoComplete}
        cols={cols}
        defaultValue={defaultValue || undefined}
        disabled={disabled}
        error={error}
        full={full}
        id={label || null}
        maxLength={maxLength}
        minLength={minLength}
        name={name}
        placeholder={placeholder}
        rows={rows}
        spellCheck={spellCheck}
        value={value}
        wrap={wrap}
        onBlur={onBlur}
        onChange={onChange}
        onClick={onClick}
        onFocus={onFocus}
        onInvalid={onInvalid}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
      />
      {message && (
        <Text
          className={twJoin(
            'relative mt-1 block text-xs lg:text-sm',
            error
              ? 'text-error-300 dark:text-error-200'
              : 'text-neutral-low-100 dark:text-neutral-high-500'
          )}
        >
          {message}
        </Text>
      )}
    </div>
  )
);

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  /**
   * Receives any HTML attr `autocomplete` value
   *
   * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#Values
   */
  autoComplete: PropTypes.string,
  /**
   * The visible width of the text control, in average character widths.
   * If it is specified, it must be a positive integer.
   */
  cols: PropTypes.number,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Default value for uncontrolled component
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Whether the input field is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether the input should render a error state
   */
  error: PropTypes.bool,
  /**
   * Renders input as a full width
   */
  full: PropTypes.bool,
  /**
   * Text to be rendered as a label
   */
  label: PropTypes.string,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Receives HTML attr `name` value
   */
  name: PropTypes.string,
  /**
   * Support message to be rendered below the input field
   */
  message: PropTypes.string,
  /**
   * Maximum length of value
   */
  maxLength: PropTypes.number,
  /**
   * Minimum length of value
   */
  minLength: PropTypes.number,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Value of the input
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Text that appears in the form control when the it is empty
   */
  placeholder: PropTypes.string,
  /**
   * Whether input has to contain a value to be submittable
   */
  required: PropTypes.bool,
  /**
   * The number of visible text lines for the control
   */
  rows: PropTypes.number,
  /**
   * Specifies whether the textarea is subject to spell checking by
   * the underlying browser/OS
   *
   * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/spellcheck
   */
  spellCheck: PropTypes.bool,
  /**
   * Indicates how the control wraps text
   */
  wrap: PropTypes.oneOf(WRAP_TYPES),
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onKeyDown` event
   */
  onKeyDown: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onKeyUp` event
   */
  onKeyUp: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onFocus` event
   */
  onFocus: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onBlur` event
   */
  onBlur: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onClick` event
   */
  onClick: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onChange` event
   */
  onChange: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onInvalid` event
   */
  onInvalid: PropTypes.func,
};

export default Textarea;
